'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/frontier-extra'

const BootstrapCompare = dynamic(() => import('../_components/widgets/FrontierExtraWidgets').then(m => m.BootstrapCompare), { ssr: false })

export default function Evaluation() {
  return (
    <Lesson>
      <Step>
        <H>How good is it, really?</H>
        <P>
          For a digit classifier, “how good” was easy: accuracy on held-out digits. For a model that writes essays, code and
          proofs, it’s one of the hardest open problems in AI. A good evaluation has to be relevant to real use, resistant to
          cheating, and precise enough to tell two models apart.
        </P>
        <UL>
          <li>
            <strong>Benchmarks</strong>: fixed question sets with known answers (multiple choice, maths, code with tests).
          </li>
          <li>
            <strong>Human preference</strong>: people compare two answers side by side and pick the better one.
          </li>
          <li>
            <strong>Model graders</strong>: a strong model scores answers against a rubric. It’s cheap and scales, but has
            biases of its own.
          </li>
        </UL>
      </Step>

      <Step gate="pass-at-k">
        <H>Scoring code: pass@k</H>
        <P>
          Code can be checked by running it. Generate n solutions per problem, count how many (c) pass the tests, and estimate
          the chance that at least one of k attempts would succeed. That rewards models that solve a problem sometimes, not
          only every time.
        </P>
        <MB>{'\\text{pass@}k = 1 - \\binom{n - c}{k} \\Big/ \\binom{n}{k}'}</MB>
        <Exercise
          id="pass-at-k"
          title="passAtK"
          {...EX.passAtK}
          hints={[
            <>
              If <C>{'n - c < k'}</C>, every set of k samples includes a passing one: return 1.
            </>,
            <>
              Otherwise <C>{'for (let i = 0; i < k; i++) ratio *= (n - c - i) / (n - i)'}</C>, and return <C>1 − ratio</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate={['bootstrap', 'q-noise']}>
        <H>Is the difference real?</H>
        <P>
          Model B scores 74% and model A 70%. Is B better? Maybe, or maybe B got lucky with this particular set of questions.
          With a few hundred questions, a few points of difference can easily be noise. The <strong>bootstrap</strong> measures
          that: re-draw the question set many times (with replacement) and see how much the gap moves.
        </P>
        <Figure n="10.12" wide caption="Two models, one benchmark. Drag the number of questions and press ‘New test set’: with small benchmarks, rankings flip by chance.">
          <BootstrapCompare />
        </Figure>
        <Exercise
          id="bootstrap"
          title="bootstrapInterval"
          {...EX.bootstrap}
          hints={[
            <>
              Repeat <C>iters</C> times: draw <C>n</C> indices with <C>random.int(0, n)</C>, sum <C>a[j]</C> and <C>b[j]</C>,
              store <C>{'(sb - sa) / n'}</C>.
            </>,
            <>
              Sort the differences and return the values at positions <C>{'Math.floor(iters * 0.025)'}</C> and{' '}
              <C>{'Math.floor(iters * 0.975)'}</C>.
            </>,
          ]}
        />
        <Quiz
          id="q-noise"
          question="A leaderboard shows model X at 81.2% and model Y at 80.9% on a 500-question benchmark. What should you conclude?"
          options={[
            { label: 'Nothing yet: the gap is far smaller than the noise on 500 questions', correct: true },
            { label: 'X is better', why: 'A 0.3-point gap on 500 questions is far inside the error bars, which span several points either way.' },
            { label: 'Y is better', why: 'The data doesn’t support either ranking.' },
          ]}
        />
      </Step>

      <Step gate="elo">
        <H>Arenas: ranking by preference</H>
        <P>
          Some qualities, like helpfulness, tone or taste, have no answer key. Arenas show two anonymous models’ answers to the
          same prompt, let people vote, and turn thousands of votes into ratings with the <strong>Elo</strong> system from chess.
          Beating a strong opponent earns more points than beating a weak one.
        </P>
        <MB>{'P(A \\text{ wins}) = \\frac{1}{1 + 10^{(R_B - R_A)/400}} \\qquad R_A \\leftarrow R_A + K\\,(\\text{result} - P)'}</MB>
        <Exercise
          id="elo"
          title="expected and update"
          {...EX.elo}
          hints={[
            <>
              <C>{'1 / (1 + 10 ** ((rb - ra) / 400))'}</C>
            </>,
            <>
              <C>{'const delta = K * ((aWon ? 1 : 0) - expected(ra, rb))'}</C>; return <C>{'[ra + delta, rb - delta]'}</C>.
            </>,
          ]}
        />
        <Callout kind="note">
          Human votes have biases too. People tend to prefer longer, more confident and nicely formatted answers, even when
          they’re less accurate. Good arenas try to control for this.
        </Callout>
      </Step>

      <Step gate={['contamination', 'q-goodhart']}>
        <H>When the test leaks</H>
        <P>
          Models are trained on huge scrapes of the web, and benchmarks are on the web. If test questions (or their answers)
          leaked into the training data, a high score shows memory, not ability. This is <strong>contamination</strong>, and
          checking for it is part of every serious evaluation.
        </P>
        <Exercise
          id="contamination"
          title="ngrams and overlap"
          {...EX.contamination}
          hints={[
            <>
              Lowercase, split on spaces, and add <C>{'words.slice(i, i + n).join(" ")'}</C> for every starting position.
            </>,
            <>
              <C>{'[...t].filter(g => seen.has(g)).length / t.size'}</C>
            </>,
          ]}
        />
        <Quiz
          id="q-goodhart"
          question="“When a measure becomes a target, it ceases to be a good measure.” What does this mean for benchmarks?"
          options={[
            { label: 'Once labs optimise for a benchmark, gains on it stop reflecting real ability — so benchmarks need constant renewal', correct: true },
            { label: 'Benchmarks should never be published', why: 'Then nobody could check results. The point is that any fixed target eventually gets gamed.' },
            { label: 'Higher scores always mean better models', why: 'That’s exactly what Goodhart’s law warns against.' },
          ]}
          explain="This is Goodhart’s law. You’ll meet it again in the next lesson, inside training itself, as reward hacking."
        />
      </Step>
    </Lesson>
  )
}
